import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2, AlertCircle, RefreshCw, TrendingUp, Calendar,
  Star, MapPin, BarChart3, ThumbsUp, Users, MessageSquare
} from 'lucide-react';
import { vendorApi } from '../services/api';
import { VendorSatisfactionTrend } from '../types';
import { extractApiError } from '../utils/errorUtils';

/**
 * VendorSatisfactionTrends - Display vendor satisfaction ratings over time (Issue #682)
 *
 * Shows historical satisfaction data across multiple fairs to help vendors
 * track their performance and identify trends.
 */
export default function VendorSatisfactionTrends() {
  const { vendorId } = useParams<{ vendorId: string }>();
  const [trends, setTrends] = useState<VendorSatisfactionTrend[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTrends = useCallback(async (isRefresh = false) => {
    if (!vendorId) return;

    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const data = await vendorApi.getVendorSatisfactionTrends(parseInt(vendorId));
      setTrends(data);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [vendorId]);

  useEffect(() => {
    loadTrends();
  }, [loadTrends]);

  const handleRefresh = () => {
    loadTrends(true);
  };

  const calculateAverage = (values: (number | null | undefined)[]): number | null => {
    const validValues = values.filter(v => v !== null && v !== undefined) as number[];
    if (validValues.length === 0) return null;
    return validValues.reduce((sum, val) => sum + val, 0) / validValues.length;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const renderRatingBar = (rating: number | null | undefined, maxRating: number = 5) => {
    if (rating === null || rating === undefined) {
      return <span className="text-sm text-gray-400">N/A</span>;
    }

    const percentage = (rating / maxRating) * 100;
    const colorClass =
      rating >= 4.5 ? 'bg-teal-500' :
      rating >= 3.5 ? 'bg-moonbone0' :
      rating >= 2.5 ? 'bg-yellow-500' :
      'bg-red-500';

    return (
      <div className="flex items-center gap-3">
        <div className="flex-1 bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full ${colorClass}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-sm font-medium text-gray-700 w-12">{rating.toFixed(1)}</span>
      </div>
    );
  };

  if (isLoading && trends.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (error && trends.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Trends</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => loadTrends()}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (trends.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-moonbone border border-teal-200 rounded-lg p-8 text-center">
          <BarChart3 className="w-16 h-16 text-secondary mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-ink mb-2">No Data Yet</h3>
          <p className="text-primary-dark mb-4">
            Complete post-fair surveys to see your satisfaction trends over time.
          </p>
          <p className="text-sm text-secondary">
            Trends help you track your performance and identify areas for improvement.
          </p>
        </div>
      </div>
    );
  }

  // Calculate overall averages
  const avgSatisfaction = calculateAverage(trends.map(t => t.satisfaction_rating));
  const avgLocation = calculateAverage(trends.map(t => t.booth_location_rating));
  const avgOrganization = calculateAverage(trends.map(t => t.fair_organization_rating));
  const returnRate = trends.filter(t => t.would_return).length / trends.length * 100;

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Satisfaction Trends</h1>
          <p className="text-gray-600">Your performance across {trends.length} fair{trends.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Overall Average Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-5 h-5 text-secondary" />
            <p className="text-sm font-medium text-teal-900">Avg Satisfaction</p>
          </div>
          <p className="text-3xl font-bold text-primary-dark">
            {avgSatisfaction ? avgSatisfaction.toFixed(1) : 'N/A'}
          </p>
          <p className="text-xs text-secondary mt-1">out of 5.0</p>
        </div>

        <div className="bg-gradient-to-br from-teal-50 to-moonbone border border-teal-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-5 h-5 text-secondary" />
            <p className="text-sm font-medium text-ink">Avg Location</p>
          </div>
          <p className="text-3xl font-bold text-primary-dark">
            {avgLocation ? avgLocation.toFixed(1) : 'N/A'}
          </p>
          <p className="text-xs text-secondary mt-1">out of 5.0</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-5 h-5 text-purple-600" />
            <p className="text-sm font-medium text-purple-900">Avg Organization</p>
          </div>
          <p className="text-3xl font-bold text-purple-700">
            {avgOrganization ? avgOrganization.toFixed(1) : 'N/A'}
          </p>
          <p className="text-xs text-purple-600 mt-1">out of 5.0</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <ThumbsUp className="w-5 h-5 text-green-600" />
            <p className="text-sm font-medium text-green-900">Return Rate</p>
          </div>
          <p className="text-3xl font-bold text-green-700">
            {returnRate.toFixed(0)}%
          </p>
          <p className="text-xs text-green-600 mt-1">would return</p>
        </div>
      </div>

      {/* Fair-by-Fair Breakdown */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Fair-by-Fair Performance</h2>
        </div>

        <div className="divide-y divide-gray-200">
          {trends.map((trend, index) => (
            <div key={`${trend.fair_id}-${index}`} className="p-6 hover:bg-gray-50 transition-colors">
              {/* Fair Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{trend.fair_name}</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    {formatDate(trend.fair_date)}
                  </div>
                </div>
                {trend.would_return && (
                  <span className="flex items-center gap-1 px-3 py-1 bg-teal-100 text-primary-dark rounded-full text-sm font-medium">
                    <ThumbsUp className="w-3 h-3" />
                    Would Return
                  </span>
                )}
              </div>

              {/* Ratings */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-2 block">
                    <Star className="w-3 h-3 inline mr-1" />
                    Overall Satisfaction
                  </label>
                  {renderRatingBar(trend.satisfaction_rating)}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-2 block">
                    <MapPin className="w-3 h-3 inline mr-1" />
                    Booth Location
                  </label>
                  {renderRatingBar(trend.booth_location_rating)}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-2 block">
                    <BarChart3 className="w-3 h-3 inline mr-1" />
                    Fair Organization
                  </label>
                  {renderRatingBar(trend.fair_organization_rating)}
                </div>
              </div>

              {/* Engagement Metrics */}
              {(trend.foot_traffic_estimate || trend.customer_interactions) && (
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                  {trend.foot_traffic_estimate !== null && trend.foot_traffic_estimate !== undefined && (
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        <span className="font-semibold text-gray-900">{trend.foot_traffic_estimate.toLocaleString()}</span> visitors
                      </span>
                    </div>
                  )}
                  {trend.customer_interactions !== null && trend.customer_interactions !== undefined && (
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        <span className="font-semibold text-gray-900">{trend.customer_interactions.toLocaleString()}</span> interactions
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Trend Insights */}
      {trends.length >= 2 && (
        <div className="mt-6 bg-moonbone border border-teal-200 rounded-lg p-6">
          <h3 className="font-semibold text-ink mb-2 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Insights
          </h3>
          <ul className="space-y-2 text-sm text-ink">
            {avgSatisfaction && avgSatisfaction >= 4.5 && (
              <li>• Your overall satisfaction rating is excellent! Keep up the great work.</li>
            )}
            {avgSatisfaction && avgSatisfaction < 3.0 && (
              <li>• Your satisfaction ratings could be improved. Consider reviewing your booth setup and products.</li>
            )}
            {returnRate >= 80 && (
              <li>• You have a high return rate - fairs are working well for you!</li>
            )}
            {avgLocation && avgLocation < 3.0 && (
              <li>• Booth location ratings are low. Consider requesting better locations at future fairs.</li>
            )}
            {trends.length >= 3 && (
              <li>• You've participated in {trends.length} fairs - great job building your presence!</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
